import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ApiServices from "../../shared/ApiServices/ApiServices";
import PatientDetails from "./PatientDetails";
import Documents from "./Documents/Documents";
import { V2Button } from "../../shared/custom-ui/v2-components";
import AdministrativeDetails from "./AdministrativeDetails";
import Skeleton from "../../shared/custom-ui/skeleton/Worklist/Skeleton";
import MedicalHistory from "./MedicalHistory";
import CurrentMedicalInformation from "./CurrentMedicalInformation";
import DocumentationAndConsent from "./DocumentationAndConsent";
import Notes from "./Notes/Notes";
import { PlaceOrderModal } from '../Lab/OrderManagement';
import './PatientProfile.css'

import { User, Calendar, FileText, Shield, ClipboardList, Activity, FlaskConical, FileSpreadsheet, Heart, ArrowLeft, } from "lucide-react";

const Icons = { User, Calendar, FileText, Shield, ClipboardList, Activity, FlaskConical, FileSpreadsheet, Heart, ArrowLeft } as any;

export default function PatientProfile() {
    const { Patientid } = useParams();
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("Personal information");
    const [labOrderOpen, setLabOrderOpen] = useState(false);

    const tabFieldMap: Record<string, string> = {
        Documents: "Documents",
        "Administrative Details": "AdministrativeDetails",
        "Medical History": "MedicalHistory",
        "Current Medical Information": "CurrentMedicalInformation",
        "Ongoing Treatment Plans": "OngoingTreatmentPlans",
        "Documentation and Consent": "DocumentationConsent",
        Notes: "Notes",
    };

    const currentField = tabFieldMap[activeTab] || "";

    useEffect(() => {
        const fetchProfileData = async () => {
            setLoading(true);
            try {
                const data = await ApiServices.getPatientProfile(Patientid, [""]);
                if (data?.patient) {
                    setProfileData(data.patient);
                } else {
                    setProfileData(null);
                }
                console.log("Fetched patient profile:", data);
            } catch {
                toast.error("Failed to fetch patient profile. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        if (Patientid) fetchProfileData();
    }, [Patientid]);

    const calculateAge = (birthDate: string | undefined) => {
        if (!birthDate) return null;
        const today = new Date();
        const dob = new Date(birthDate);
        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
            age--;
        }
        return age;
    };

    const age = calculateAge(profileData?.BirthDate);

    const tabs = [
        { name: "Personal information", icon: User },
        { name: "Documents", icon: FileText },
        { name: "Administrative Details", icon: Shield },
        { name: "Medical History", icon: Activity },
        { name: "Current Medical Information", icon: ClipboardList },
        { name: "Documentation and Consent", icon: FileSpreadsheet },
        { name: "Notes", icon: FlaskConical },
    ];

    const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const tabsContainerRef = useRef<HTMLDivElement | null>(null);
    const isDragging = useRef(false);
    const dragStartX = useRef(0);
    const scrollStartX = useRef(0);

    const onSelectTab = (name: string) => {
        setActiveTab(name);
        requestAnimationFrame(() => {
            const btn = tabRefs.current[name];
            const container = tabsContainerRef.current;
            if (btn && container) {
                const btnRect = btn.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                const offset = btnRect.left - containerRect.left - (containerRect.width / 2 - btnRect.width / 2);
                container.scrollBy({ left: offset, behavior: 'smooth' });
            }
        });
    };

    const handleWheel = useCallback((e: WheelEvent) => {
        if (!tabsContainerRef.current) return;
        if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) {
            tabsContainerRef.current.scrollLeft += e.deltaY;
            e.preventDefault();
        }
    }, []);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!tabsContainerRef.current) return;
        isDragging.current = true;
        dragStartX.current = e.clientX;
        scrollStartX.current = tabsContainerRef.current.scrollLeft;
        tabsContainerRef.current.classList.add('grabbing');
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging.current || !tabsContainerRef.current) return;
        const dx = e.clientX - dragStartX.current;
        tabsContainerRef.current.scrollLeft = scrollStartX.current - dx;
    };

    const endDrag = () => {
        if (!isDragging.current || !tabsContainerRef.current) return;
        isDragging.current = false;
        tabsContainerRef.current.classList.remove('grabbing');
    };

    useEffect(() => {
        const el = tabsContainerRef.current;
        if (!el) return;
        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            el.removeEventListener('wheel', handleWheel as any);
        };
    }, [handleWheel]);

    return (
        <>
        <div className="space-y-4">
            {/* Header Card matching ModernPageHeader styling */}
            <div
                className="relative overflow-hidden rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] shadow-card px-6 py-4"
            >
                {/* Brand Hero Tint */}
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: 'var(--v2-bg-hero)' }}
                />

                {/* Top Branded Accent Line */}
                <div
                    aria-hidden
                    className="absolute inset-x-0 top-0 h-[3px]"
                    style={{
                        background:
                            'linear-gradient(90deg, var(--v2-primary-500) 0%, var(--v2-primary-400) 55%, transparent 100%)',
                    }}
                />

                {/* Card Content Container */}
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-center gap-4 w-full md:w-auto">
                        <V2Button
                            variant="outline"
                            size="small"
                            onClick={() => navigate(-1)}
                            className="!rounded-full !w-8 !h-8 !p-0 !min-h-0 flex-shrink-0 hover:!bg-[var(--v2-bg-hover)] border-[var(--v2-border-interactive)]"
                            leftIcon={<Icons.ArrowLeft className="w-4.5 h-4.5 text-[var(--v2-text-secondary)]" />}
                            title="Go back"
                        />

                        {/* Avatar */}
                        {loading ? (
                            <Skeleton variant="circle" width={48} height={48} className="flex-shrink-0" />
                        ) : (
                            <div 
                                className="w-12 h-12 rounded-full flex items-center justify-center text-base font-bold shadow-sm flex-shrink-0 border"
                                style={{
                                    background: 'var(--v2-primary-50, rgba(4,94,184,0.08))',
                                    color: 'var(--v2-primary-500)',
                                    borderColor: 'var(--v2-primary-100, rgba(4,94,184,0.15))'
                                }}
                            >
                                {profileData?.PatientName
                                    ? profileData.PatientName.split(" ")
                                        .map((n: string) => n[0])
                                        .slice(0, 2)
                                        .join("")
                                        .toUpperCase()
                                    : "NA"}
                            </div>
                        )}

                        {/* Patient Information Panel */}
                        <div className="flex-1 min-w-0">
                            {loading ? (
                                <div className="space-y-2 w-full">
                                    <Skeleton variant="title" width={160} height={20} className="!rounded-md" />
                                    <Skeleton variant="text" width={200} height={16} className="!rounded-md" />
                                </div>
                            ) : (
                                <>
                                    <h1 className="text-xl font-bold tracking-tight text-[var(--v2-text-heading)] mb-1 truncate">
                                        {profileData?.PatientName || "Patient Name"}
                                    </h1>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[var(--v2-text-secondary)]">
                                        <span className="flex items-center gap-1.5">
                                            <Icons.User className="w-3.5 h-3.5 text-[var(--v2-primary-400)]" />
                                            <span className="font-semibold text-[var(--v2-text-primary)]">MRN:</span>
                                            <span>{profileData?.MRN || "N/A"}</span>
                                        </span>
                                        <span className="hidden sm:inline text-[var(--v2-text-muted)] opacity-50" aria-hidden>•</span>
                                        <span className="flex items-center gap-1.5">
                                            <Icons.Activity className="w-3.5 h-3.5 text-[var(--v2-primary-400)]" />
                                            <span>
                                                {profileData?.Sex === "M" && "Male"}
                                                {profileData?.Sex === "F" && "Female"}
                                                {(profileData?.Sex === "M" || profileData?.Sex === "F") && age ? ", " : ""}
                                                {age ? `Age ${age}` : ""}
                                                {profileData?.Sex !== "M" && profileData?.Sex !== "F" && !age ? "N/A" : ""}
                                            </span>
                                        </span>
                                        <span className="hidden sm:inline text-[var(--v2-text-muted)] opacity-50" aria-hidden>•</span>
                                        <span className="flex items-center gap-1.5">
                                            <Icons.Heart className="w-3.5 h-3.5 text-[var(--v2-primary-400)]" />
                                            <span className="font-semibold text-[var(--v2-text-primary)]">Blood:</span>
                                            <span>{profileData?.BloodType || "Not Recorded"}</span>
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-2 flex-shrink-0">
                        <V2Button
                            variant="secondary"
                            size="small"
                            leftIcon={<Icons.FlaskConical className="w-[15px] h-[15px]" />}
                            onClick={() => setLabOrderOpen(true)}
                            disabled={!profileData}
                        >
                            Lab Order
                        </V2Button>
                        <V2Button
                            variant="primary"
                            size="small"
                            leftIcon={<Icons.Calendar className="w-[18px] h-[18px]" />}
                            onClick={() => {
                                if (profileData) {
                                    navigate('/scheduling/add-order', { state: { selectedPatient: profileData } });
                                } else {
                                    navigate('/scheduling/add-order');
                                }
                            }}>
                            Schedule
                        </V2Button>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="relative">
                <div
                    ref={tabsContainerRef}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={endDrag}
                    onMouseUp={endDrag}
                    className="border rounded-xl shadow-card p-1.5 flex gap-1 overflow-x-auto hide-scrollbar whitespace-nowrap snap-x snap-mandatory scroll-smooth select-none cursor-grab"
                    style={{ background: 'var(--v2-bg-card)', borderColor: 'var(--v2-border-card)' }}
                >
                    {tabs.map((tab) => {
                        const Icon = tab.icon as React.ElementType;
                        const isActive = activeTab === tab.name;
                        return (
                            <button
                                key={tab.name}
                                ref={(el) => (tabRefs.current[tab.name] = el)}
                                onClick={() => onSelectTab(tab.name)}
                                className={`flex items-center flex-none px-4 py-1.5 rounded-full text-sm font-semibold transition-all snap-center ${
                                    isActive
                                        ? "text-white shadow-[0_2px_4px_rgba(4,94,184,0.2)]"
                                        : "text-[var(--v2-text-secondary)] hover:text-[var(--v2-text-primary)] hover:bg-[var(--v2-bg-hover)]"
                                }`}
                                style={isActive ? { background: 'linear-gradient(to bottom, var(--v2-primary-500), var(--v2-primary-600))' } : undefined}
                            >
                                <Icon className="w-4 h-4 mr-2" />
                                {tab.name}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Section Content */}
            <div className="border rounded-2xl shadow-card" style={{ background: 'var(--v2-bg-card)', borderColor: 'var(--v2-border-card)' }}>
                {activeTab === "Personal information" && (
                    <PatientDetails Patientid={Patientid} initialData={profileData ?? undefined} />
                )}
                {activeTab === "Documents" && <Documents Patientid={Patientid} />}
                {activeTab === "Administrative Details" && (
                    <AdministrativeDetails Patientid={Patientid} fields={currentField} />
                )}
                {activeTab === "Medical History" && (
                    <MedicalHistory Patientid={Patientid} fields={currentField} />
                )}
                {activeTab === "Current Medical Information" && (
                    <CurrentMedicalInformation
                        Patientid={Patientid}
                        fields={currentField}
                    />
                )}
                {activeTab === "Documentation and Consent" && (
                    <DocumentationAndConsent
                        Patientid={Patientid}
                        fields={currentField}
                    />
                )}
                {activeTab === "Notes" && <Notes />}
            </div>
        </div>

        {/* ══════ PLACE NEW LAB ORDER MODAL ══════ */}
        {labOrderOpen && (
            <PlaceOrderModal
                initialPatient={profileData}
                onClose={() => setLabOrderOpen(false)}
                onSuccess={() => setLabOrderOpen(false)}
            />
        )}
        </>
    );
}
